import { Box, IconButton, TextField, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectVehicleMeasures } from "../../../redux/selector";
import { actions } from "../../../redux/slice";
import { VehicleMeasureCode } from "../../../types";
import {
  VEHICLE_MEASURES_COLUMNS,
  VEHICLE_MEASURES_SECTION_TITLE,
  VEHICLE_MEASURE_ROWS,
} from "../data";
import { useStyles } from "./styles";

const VehicleMeasuresPanel = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isEnglish = !(i18n.language || "").startsWith("he");
  const vehicleMeasures = useSelector(selectVehicleMeasures);
  const styles = useStyles();

  if (!vehicleMeasures) return null;

  const updateValue = (key: VehicleMeasureCode, value: string) =>
    dispatch(actions.updateVehicleMeasureValue({ key, value }));

  const updateGCalculation = (delta: number) => {
    const numeric = Number(vehicleMeasures.g.calculationValue) || 0;
    dispatch(
      actions.updateVehicleMeasureCalculationValue({
        key: "g",
        calculationValue: String(Math.max(0, numeric + delta)),
      })
    );
  };

  return (
    <Box className={styles.panelSection}>
      <Typography className={styles.panelSectionTitle}>
        {t(VEHICLE_MEASURES_SECTION_TITLE)}
      </Typography>

      <Box className={styles.chassisTable}>
        {VEHICLE_MEASURE_ROWS.map((row) => (
          <Box key={row.key} className={styles.chassisDataRow}>
              <Box className={styles.chassisFieldCell}>
                <Typography className={styles.chassisFieldLabel}>
                  {t(VEHICLE_MEASURES_COLUMNS.symbol)}
                </Typography>
                <Typography className={styles.chassisSymbolValue}>{row.symbol}</Typography>
              </Box>
              <Box className={styles.chassisFieldCell}>
                <Typography className={styles.chassisFieldLabel}>
                  {t(VEHICLE_MEASURES_COLUMNS.description)}
                </Typography>
                <Box className={styles.vehicleMeasureDescriptionWrap}>
                  <Typography className={styles.vehicleMeasureDescriptionText}>
                    {t(row.descriptionKey)}
                  </Typography>
                  {row.withAdjuster && (
                    <Box className={styles.vehicleMeasureInlineAdjuster}>
                      <Typography className={styles.vehicleMeasureInlineNote}>
                        {row.noteKey ? t(row.noteKey) : ""}
                      </Typography>
                      <IconButton
                        className={`${styles.vehicleMeasureInlineButton} ${styles.vehicleMeasureInlineButtonMinus}`}
                        aria-label={t("WORK_ORDER.CENTRE_OF_GRAVITY.BUTTONS.DECREASE")}
                        onClick={() => updateGCalculation(-1)}
                      >
                        <Typography className={styles.vehicleMeasureInlineIcon}>−</Typography>
                      </IconButton>
                      <IconButton
                        className={`${styles.vehicleMeasureInlineButton} ${styles.vehicleMeasureInlineButtonPlus}`}
                        aria-label={t("WORK_ORDER.CENTRE_OF_GRAVITY.BUTTONS.INCREASE")}
                        onClick={() => updateGCalculation(1)}
                      >
                        <Typography className={styles.vehicleMeasureInlineIcon}>+</Typography>
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box className={styles.chassisInputGroup}>
                <Box
                  className={styles.chassisInputFieldCell}
                  style={isEnglish ? { width: "auto", minWidth: "auto" } : {}}
                >
                  <Typography
                    className={styles.chassisFieldLabel}
                    style={isEnglish ? { whiteSpace: "nowrap" } : {}}
                  >
                    {t(VEHICLE_MEASURES_COLUMNS.value)}
                  </Typography>
                  <TextField
                    className={styles.chassisValueInput}
                    variant="outlined"
                    value={vehicleMeasures[row.key].value}
                    onChange={(event) => updateValue(row.key, event.target.value)}
                  />
                </Box>
                <Box
                  className={styles.chassisInputFieldCell}
                  style={isEnglish ? { width: "auto", minWidth: "auto" } : {}}
                >
                  <Typography
                    className={styles.chassisFieldLabel}
                    style={isEnglish ? { whiteSpace: "nowrap" } : {}}
                  >
                    {t(VEHICLE_MEASURES_COLUMNS.calculation)}
                  </Typography>
                  <Box className={styles.chassisCalculationValue}>
                    {vehicleMeasures[row.key].calculationValue}
                  </Box>
                </Box>
              </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default VehicleMeasuresPanel;
